/**
 * 分类管理列表
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Plus,
  Loader2,
  FolderTree,
  Pencil,
  Trash2,
  GripVertical,
  ImageIcon,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { useToast } from '@/shared/hooks/useToast'
import { CategoryApiService } from '../services/app-api'
import type { Category } from '../types/app'
import CategoryDialog from './CategoryDialog'

interface CategoryListProps {
  appId: string
}

export default function CategoryList({ appId }: CategoryListProps) {
  const { showSuccess, showError } = useToast()

  // 状态
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSavingOrder, setIsSavingOrder] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  // 加载分类列表
  const loadCategories = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await CategoryApiService.getCategories(appId)
      if (response.success && response.data) {
        // 按 sort_order 排序
        const sorted = [...response.data.categories].sort(
          (a, b) => a.sort_order - b.sort_order
        )
        setCategories(sorted)
      }
    } catch (error) {
      console.error('❌ [CategoryList] 加载分类列表失败:', error)
      showError('加载分类列表失败')
    } finally {
      setIsLoading(false)
    }
  }, [appId, showError])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  // 创建/编辑成功回调
  const handleDialogSuccess = (category: Category) => {
    if (editingCategory) {
      // 编辑：更新列表中的分类
      setCategories((prev) =>
        prev.map((c) => (c.category_id === category.category_id ? category : c))
      )
    } else {
      // 创建：添加到列表
      setCategories((prev) =>
        [...prev, category].sort((a, b) => a.sort_order - b.sort_order)
      )
    }
    setIsDialogOpen(false)
    setEditingCategory(null)
  }

  // 删除分类
  const handleDelete = async () => {
    if (!categoryToDelete) return

    setIsDeleting(true)
    try {
      const response = await CategoryApiService.deleteCategory(
        appId,
        categoryToDelete.category_id
      )
      if (response.success) {
        showSuccess(`分类「${categoryToDelete.name}」已删除`)
        setCategories((prev) =>
          prev.filter((c) => c.category_id !== categoryToDelete.category_id)
        )
        setCategoryToDelete(null)
      }
    } catch (error) {
      console.error('❌ [CategoryList] 删除分类失败:', error)
      showError('删除分类失败')
    } finally {
      setIsDeleting(false)
    }
  }

  // 拖拽排序
  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newCategories = [...categories]
    const [dragged] = newCategories.splice(draggedIndex, 1)
    newCategories.splice(index, 0, dragged)
    setCategories(newCategories)
    setDraggedIndex(index)
  }

  const handleDragEnd = async () => {
    if (draggedIndex === null) return

    setDraggedIndex(null)
    setIsSavingOrder(true)

    try {
      // 更新 sort_order
      const reorderData = categories.map((c, index) => ({
        category_id: c.category_id,
        sort_order: index,
      }))

      const response = await CategoryApiService.reorderCategories(appId, {
        categories: reorderData,
      })

      if (response.success) {
        showSuccess('分类排序已更新')
        // 更新本地数据的 sort_order
        setCategories((prev) =>
          prev.map((c, index) => ({ ...c, sort_order: index }))
        )
      }
    } catch (error) {
      console.error('❌ [CategoryList] 保存排序失败:', error)
      showError('保存排序失败')
      // 重新加载以恢复原始顺序
      loadCategories()
    } finally {
      setIsSavingOrder(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>分类管理</CardTitle>
          <CardDescription>管理 App 的项目分类，拖拽可调整排序</CardDescription>
        </div>
        <Button
          onClick={() => {
            setEditingCategory(null)
            setIsDialogOpen(true)
          }}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          创建分类
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <FolderTree className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
              暂无分类
            </h3>
            <p className="text-slate-500 mb-4 max-w-sm">
              创建分类来组织你的项目，方便用户浏览和发现。
            </p>
            <Button
              onClick={() => {
                setEditingCategory(null)
                setIsDialogOpen(true)
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              创建第一个分类
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {isSavingOrder && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                保存排序中...
              </div>
            )}
            {categories.map((category, index) => (
              <div
                key={category.category_id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-4 p-4 border rounded-lg bg-white dark:bg-slate-900 hover:shadow-sm transition-shadow cursor-move ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
              >
                <GripVertical className="h-5 w-5 text-slate-300 flex-shrink-0" />
                
                {category.icon_url ? (
                  <img
                    src={category.icon_url}
                    alt={category.name}
                    className="h-10 w-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="h-5 w-5 text-primary" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{category.name}</span>
                    <span className="text-xs text-slate-400">#{category.category_id}</span>
                  </div>
                  {category.description && (
                    <p className="text-sm text-slate-500 truncate">{category.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingCategory(category)
                      setIsDialogOpen(true)
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation()
                      setCategoryToDelete(category)
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* 创建/编辑弹窗 */}
      <CategoryDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open)
          if (!open) setEditingCategory(null)
        }}
        appId={appId}
        category={editingCategory}
        onSuccess={handleDialogSuccess}
      />

      {/* 删除确认弹窗 */}
      <AlertDialog
        open={!!categoryToDelete}
        onOpenChange={(open) => !open && setCategoryToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除分类</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除分类「{categoryToDelete?.name}」吗？删除后，已发布到该分类的项目不会被自动移除，但需要重新设置分类。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  删除中...
                </>
              ) : (
                '删除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
